import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Configuración de breadcrumbs para hacer el contexto más flexible
export interface BreadcrumbConfig {
  id: string;
  label: string;
  pathPatterns: string[];
  isVisible?: boolean;
}

// Configuración por defecto - puede ser sobrescrita
const DEFAULT_BREADCRUMB_CONFIG: BreadcrumbConfig[] = [
  {
    id: 'initials',
    label: 'Iniciales',
    pathPatterns: ['INTERIOR', 'DOCUMENTS', 'VIDEO', 'ACCESSORIES', 'OTHERS'],
    isVisible: true,
  },
  {
    id: 'externas',
    label: 'Externas',
    pathPatterns: ['EXTERIOR', 'SURA_PHOTOS'],
    isVisible: true,
  },
  {
    id: 'diagnostico',
    label: 'Diagnóstico',
    pathPatterns: ['DIAGNOSTICO', 'DIAGNOSTIC'],
    isVisible: false,
  },
];

export interface BreadcrumbState {
  activeBreadcrumbId: string | null;
  setActiveBreadcrumbId: (id: string | null) => void;
  isActive: (id: string) => boolean;
  getBreadcrumbConfig: () => BreadcrumbConfig[];
  updateBreadcrumbConfig: (config: BreadcrumbConfig[]) => void;
  addBreadcrumb: (config: BreadcrumbConfig) => void;
  removeBreadcrumb: (id: string) => void;
  updateBreadcrumb: (id: string, updates: Partial<BreadcrumbConfig>) => void;
  getVisibleBreadcrumbs: () => BreadcrumbConfig[];
  // Mantener compatibilidad hacia atrás
  isInitiales: boolean;
  isExternas: boolean;
}

const BreadcrumbContext = createContext<BreadcrumbState | undefined>(undefined);

interface BreadcrumbProviderProps {
  children: ReactNode;
  customConfig?: BreadcrumbConfig[];
  defaultActiveId?: string;
}

export const BreadcrumbProvider: React.FC<BreadcrumbProviderProps> = ({ 
  children, 
  customConfig,
  defaultActiveId = 'initials'
}) => {
  const location = useLocation();
  const [activeBreadcrumbId, setActiveBreadcrumbId] = useState<string | null>(defaultActiveId);
  const [breadcrumbConfig, setBreadcrumbConfig] = useState<BreadcrumbConfig[]>(
    customConfig || DEFAULT_BREADCRUMB_CONFIG
  );

  // Función para determinar el breadcrumb activo basado en la URL
  const determineActiveBreadcrumb = (path: string): string | null => {
    for (const config of breadcrumbConfig) {
      if (config.pathPatterns.some(pattern => path.includes(pattern))) {
        return config.id;
      }
    }
    return defaultActiveId;
  };

  // Sincronizar el contexto con la URL usando configuración dinámica
  useEffect(() => {
    const path = location.pathname;
    const newActiveId = determineActiveBreadcrumb(path);
    setActiveBreadcrumbId(newActiveId);
  }, [location.pathname, breadcrumbConfig]);

  // Función para verificar si un breadcrumb está activo
  const isActive = (id: string): boolean => {
    return activeBreadcrumbId === id;
  };

  // Función para obtener la configuración actual
  const getBreadcrumbConfig = (): BreadcrumbConfig[] => {
    return breadcrumbConfig;
  };

  // Función para actualizar la configuración dinámicamente
  const updateBreadcrumbConfig = (config: BreadcrumbConfig[]): void => {
    setBreadcrumbConfig(config);
  };

  // Función para agregar un nuevo breadcrumb dinámicamente
  const addBreadcrumb = (config: BreadcrumbConfig): void => {
    setBreadcrumbConfig(prev => [...prev, config]);
  };

  // Función para remover un breadcrumb por ID
  const removeBreadcrumb = (id: string): void => {
    setBreadcrumbConfig(prev => prev.filter(config => config.id !== id));
  };

  // Función para actualizar un breadcrumb existente
  const updateBreadcrumb = (id: string, updates: Partial<BreadcrumbConfig>): void => {
    setBreadcrumbConfig(prev => 
      prev.map(config => 
        config.id === id ? { ...config, ...updates } : config
      )
    );
  };

  // Función para obtener breadcrumbs visibles
  const getVisibleBreadcrumbs = (): BreadcrumbConfig[] => {
    return breadcrumbConfig.filter(config => config.isVisible !== false);
  };

  // Mantener compatibilidad hacia atrás
  const isInitiales = isActive('initials');
  const isExternas = isActive('externas');

  const value: BreadcrumbState = {
    activeBreadcrumbId,
    setActiveBreadcrumbId,
    isActive,
    getBreadcrumbConfig,
    updateBreadcrumbConfig,
    addBreadcrumb,
    removeBreadcrumb,
    updateBreadcrumb,
    getVisibleBreadcrumbs,
    isInitiales,
    isExternas,
  };

  return (
    <BreadcrumbContext.Provider value={value}>
      {children}
    </BreadcrumbContext.Provider>
  );
};

export const useBreadcrumbContext = (): BreadcrumbState => {
  const context = useContext(BreadcrumbContext);
  if (context === undefined) {
    throw new Error('useBreadcrumbContext must be used within a BreadcrumbProvider');
  }
  return context;
};

// Hook personalizado para operaciones comunes de breadcrumbs
export const useBreadcrumbActions = () => {
  const context = useBreadcrumbContext();
  
  return {
    // Acciones básicas
    setActive: context.setActiveBreadcrumbId,
    isActive: context.isActive,
    
    // Gestión de configuración
    addBreadcrumb: context.addBreadcrumb,
    removeBreadcrumb: context.removeBreadcrumb,
    updateBreadcrumb: context.updateBreadcrumb,
    
    // Consultas
    getConfig: context.getBreadcrumbConfig,
    getVisible: context.getVisibleBreadcrumbs,
    
    // Estado actual
    activeId: context.activeBreadcrumbId,
    
    // Compatibilidad hacia atrás
    isInitiales: context.isInitiales,
    isExternas: context.isExternas,
  };
};
